import type {
  DrawingToolInstance, TwoPointCoordinates, FibonacciSettings, ChartLayout, TimestampToX, PriceToY,
} from "./types";
import { applyLineStyle, drawHandleCircle } from "./drawHelpers";

const FIBONACCI_LEVELS = [
  0, 0.236, 0.382, 0.5, 0.618, 0.786, 1.0, 1.236, 1.272, 1.382, 1.5, 1.618, 1.786, 2.618,
];
const DASH_PATTERN = [6, 4];

function getLevelLabel(level: number, settings: FibonacciSettings): string {
  return settings.levelDisplay === "percents"
    ? `${(level * 100).toFixed(1)}%`
    : level.toFixed(3);
}

export function drawFibonacci(
  ctx: CanvasRenderingContext2D,
  coords: TwoPointCoordinates,
  instance: DrawingToolInstance,
  layout: ChartLayout,
  timestampToX: TimestampToX,
  priceToY: PriceToY,
  fibSettings: FibonacciSettings,
  draggingCoords?: TwoPointCoordinates,
) {
  const { point1, point2 } = draggingCoords ?? coords;
  const x1 = timestampToX(point1.timestamp);
  const y1 = priceToY(point1.price);
  const x2 = timestampToX(point2.timestamp);
  const y2 = priceToY(point2.price);
  const xLeft = Math.min(x1, x2);
  const xRight = Math.max(x1, x2);
  const chartRight = layout.width - layout.right;
  const priceDiff = point1.price - point2.price;

  FIBONACCI_LEVELS.forEach((level) => {
    const price = point2.price + priceDiff * level;
    const y = priceToY(price);
    const xL = Math.max(layout.left, xLeft);
    const xR = Math.min(chartRight, xRight);

    applyLineStyle(ctx, instance.mainLine.color, instance.mainLine.width, instance.mainLine.opacity);
    ctx.beginPath();
    ctx.moveTo(xL, y);
    ctx.lineTo(xR, y);
    ctx.stroke();

    ctx.font = "10px Arial";
    ctx.textBaseline = "middle";
    ctx.globalAlpha = 1;

    if (fibSettings.levelsVisible) {
      ctx.fillStyle = fibSettings.levelsColor;
      ctx.textAlign = "right";
      ctx.fillText(getLevelLabel(level, fibSettings), xL - 4, y);
    }
    if (fibSettings.valuesVisible) {
      ctx.fillStyle = fibSettings.valuesColor;
      ctx.textAlign = "left";
      ctx.fillText(price.toFixed(5), xR + 4, y);
    }
  });

  applyLineStyle(ctx, instance.mainLine.color, instance.mainLine.width, instance.mainLine.opacity);
  ctx.setLineDash(DASH_PATTERN);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.setLineDash([]);

  drawHandleCircle(ctx, x1, y1, instance.mainLine.color);
  drawHandleCircle(ctx, x2, y2, instance.mainLine.color);
}
